import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, Animated, Platform } from 'react-native';

import { useAppTheme } from '../../design-system/ThemeProvider';

interface Tab {
  id: string;
  label: string;
  icon: keyof typeof Ionicons.glyphMap;
  colors: string[];
}

interface CollapsibleIntroHeaderProps {
  isExpanded: boolean;
  onToggle: () => void;
  title: string;
  subtitle: string;
  tabs?: Tab[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  onInteractionStart?: () => void;
  onInteractionEnd?: () => void;
}

const CollapsibleIntroHeader: React.FC<CollapsibleIntroHeaderProps> = ({
  isExpanded,
  onToggle,
  title,
  subtitle,
  tabs = [],
  activeTab,
  onTabChange,
  onInteractionStart,
  onInteractionEnd
}) => {
  const { colors } = useAppTheme();
  // Header height when fully expanded (increased to accommodate taller buttons)
  const EXPANDED_HEIGHT = tabs.length > 2 ? 280 : tabs.length > 0 ? 240 : 120;
  
  const animatedHeight = useRef(new Animated.Value(isExpanded ? EXPANDED_HEIGHT : 0)).current;
  const scaleAnim = useRef(new Animated.Value(1)).current;
  
  // Debug logging for tabs
  useEffect(() => {
    console.log('🔍 CollapsibleIntroHeader - tabs received:', tabs);
    console.log('🔍 CollapsibleIntroHeader - tabs length:', tabs.length);
    console.log('🔍 CollapsibleIntroHeader - isExpanded:', isExpanded);
    console.log('🔍 CollapsibleIntroHeader - activeTab:', activeTab);
  }, [tabs, isExpanded, activeTab]);

  useEffect(() => {
    Animated.timing(animatedHeight, {
      toValue: isExpanded ? EXPANDED_HEIGHT : 0,
      duration: 300,
      useNativeDriver: false,
    }).start();
  }, [isExpanded, EXPANDED_HEIGHT, animatedHeight]);

  const handleToggle = () => {
    // Add subtle scale animation for premium feedback
    Animated.sequence([
      Animated.timing(scaleAnim, {
        toValue: 0.95,
        duration: 100,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 100,
        useNativeDriver: true,
      }),
    ]).start();
    
    onToggle();
  };

  return (
    <View style={{ marginBottom: 8 }}>
      {/* Collapsible Header Content */}
      <Animated.View
        style={{
          height: animatedHeight,
          overflow: 'hidden',
        }}
        onTouchStart={onInteractionStart}
        onTouchEnd={onInteractionEnd}
        onTouchCancel={onInteractionEnd}
      >
        <View style={{
          alignItems: 'center',
          paddingHorizontal: 20,
          paddingTop: 16,
          paddingBottom: 20,
        }}>
          {/* Premium Header Badge with People Icon */}
          <View style={{
            backgroundColor: colors.surfaceContainerLow,
            paddingHorizontal: 24,
            paddingVertical: 14,
            borderRadius: 16,
            marginBottom: 20,
            borderWidth: 1,
            borderColor: colors.outlineVariant,
            ...Platform.select({
              ios: {
                shadowColor: colors.black,
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.08,
                shadowRadius: 8,
              },
              android: {
                elevation: 4,
              },
              web: {
                boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
              },
            }),
          }}>
            <View style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Ionicons 
                name="people" 
                size={18} 
                color="#3B82F6" 
                style={{ marginRight: 8 }} 
              />
              <Text style={{
                fontSize: 15,
                fontWeight: '700',
                color: colors.onSurface,
                letterSpacing: 0.5,
                textAlign: 'center',
              }}>
                {title}
              </Text>
            </View>
          </View>

          {/* Premium Subtitle */}
          <Text style={{
            fontSize: 16,
            fontWeight: '500',
            color: colors.onSurfaceVariant,
            textAlign: 'center',
            lineHeight: 24,
            paddingHorizontal: 20,
            marginBottom: tabs.length > 0 ? 24 : 0,
          }}>
            {subtitle}
          </Text>

          {/* Premium Tab Navigation */}
          {tabs.length > 0 && (
            <View style={{
              flexDirection: 'row',
              paddingHorizontal: 16,
              paddingBottom: 16,
              gap: 12,
              marginTop: 8,
            }}>
              {tabs.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <TouchableOpacity
                    key={tab.id}
                    style={{
                      flex: 1,
                      borderRadius: 14,
                      overflow: 'hidden',
                      transform: [{ scale: isActive ? 1.02 : 1 }],
                      ...Platform.select({
                        ios: {
                          shadowColor: isActive ? tab.colors[0] : '#000',
                          shadowOffset: { width: 0, height: isActive ? 4 : 1 },
                          shadowOpacity: isActive ? 0.3 : 0.1,
                          shadowRadius: isActive ? 8 : 2,
                        },
                        android: {
                          elevation: isActive ? 8 : 2,
                        },
                        web: {
                          boxShadow: isActive 
                            ? `0 4px 20px ${tab.colors[0]}40, 0 2px 8px rgba(0,0,0,0.1)`
                            : '0 1px 4px rgba(0,0,0,0.08)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        },
                      }),
                    }}
                    onPress={() => onTabChange?.(tab.id)}
                    activeOpacity={0.9}
                  >
                    {isActive ? (
                      <LinearGradient
                        colors={[tab.colors[0], tab.colors[1]]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={{
                          paddingVertical: Platform.select({
                            web: 16,
                            ios: 18,
                            android: 18,
                            default: 18
                          }),
                          paddingHorizontal: Platform.select({
                            web: 16,
                            ios: 12,
                            android: 12,
                            default: 12
                          }),
                          alignItems: 'center',
                          borderRadius: 12,
                          minHeight: Platform.select({
                            web: 52,
                            ios: 60,
                            android: 60,
                            default: 60
                          }),
                          justifyContent: 'center',
                        }}
                      >
                        <View style={{
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <View style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            borderRadius: 8,
                            padding: 6,
                            marginBottom: 6,
                          }}>
                            <Ionicons name={tab.icon} size={18} color="#FFFFFF" />
                          </View>
                          <Text style={{
                            color: '#FFFFFF',
                            fontSize: Platform.select({
                              web: 12,
                              ios: 14,
                              android: 14,
                              default: 14
                            }),
                            fontWeight: '700',
                            letterSpacing: 0.2,
                            textAlign: 'center',
                            lineHeight: Platform.select({
                              web: 14,
                              ios: 16,
                              android: 16,
                              default: 16
                            }),
                          }}
                          numberOfLines={1}
                          adjustsFontSizeToFit={true}
                          minimumFontScale={0.7}
                          >
                            {tab.label}
                          </Text>
                        </View>
                      </LinearGradient>
                    ) : (
                      <View style={{
                        backgroundColor: colors.surfaceContainerHigh,
                        paddingVertical: Platform.select({
                          web: 16,
                          ios: 18,
                          android: 18,
                          default: 18
                        }),
                        paddingHorizontal: Platform.select({
                          web: 16,
                          ios: 12,
                          android: 12,
                          default: 12
                        }),
                        alignItems: 'center',
                        borderRadius: 12,
                        minHeight: Platform.select({
                          web: 52,
                          ios: 60,
                          android: 60,
                          default: 60
                        }),
                        justifyContent: 'center',
                        borderWidth: 1,
                        borderColor: colors.outlineVariant,
                      }}>
                        <View style={{
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <View style={{
                            backgroundColor: colors.surfaceContainer,
                            borderRadius: 8,
                            padding: 6,
                            marginBottom: 6,
                          }}>
                            <Ionicons name={tab.icon} size={18} color={colors.primary} />
                          </View>
                          <Text style={{
                            color: colors.onSurface,
                            fontSize: Platform.select({
                              web: 12,
                              ios: 14,
                              android: 14,
                              default: 14
                            }),
                            fontWeight: '600',
                            letterSpacing: 0.2,
                            textAlign: 'center',
                            lineHeight: Platform.select({
                              web: 14,
                              ios: 16,
                              android: 16,
                              default: 16
                            }),
                          }}
                          numberOfLines={1}
                          adjustsFontSizeToFit={true}
                          minimumFontScale={0.7}
                          >
                            {tab.label}
                          </Text>
                        </View>
                      </View>
                    )}
                  </TouchableOpacity>
                );
              })}
            </View>
          )}
        </View>
      </Animated.View>

      {/* Toggle Button */}
      <View style={{
        alignItems: 'center',
        marginTop: 4,
      }}>
        <Animated.View style={{ transform: [{ scale: scaleAnim }] }}>
          <TouchableOpacity
            onPress={handleToggle}
            style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              backgroundColor: colors.surfaceContainerLow,
              alignItems: 'center',
              justifyContent: 'center',
              borderWidth: 1,
              borderColor: colors.outlineVariant,
              ...Platform.select({
                ios: {
                  shadowColor: colors.black,
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.15,
                  shadowRadius: 8,
                },
                android: {
                  elevation: 4,
                },
                web: {
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                },
              }),
            }}
            activeOpacity={0.8}
          >
            <Animated.View
              style={{
                transform: [{
                  rotate: animatedHeight.interpolate({
                    inputRange: [0, EXPANDED_HEIGHT],
                    outputRange: ['0deg', '180deg'],
                  })
                }]
              }}
            >
              <Ionicons 
                name="chevron-down" 
                size={20} 
                color={colors.onSurface}
              />
            </Animated.View>
          </TouchableOpacity>
        </Animated.View>
      </View>
    </View>
  );
};

export default CollapsibleIntroHeader;
